import React, { useMemo } from 'react';
import { Box, Tooltip, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Table from '../../../../components/tables/Table';
import tableStyles from '../../../../components/tables/Table.styles';
import styles from './ConfigTable.styles';

import {
  labelForType,
  prettifyHeader,
  issuesForHeader,
  parseUKDate,
} from '../../utils/createConfig.helpers';

const HEAD_CELLS = [
  'Operator',
  'OperatorName',
  'Unit',
  'EVN',
  'MeterNumber',
  'MeterType',
  'FromDate',
  'Comments',
];

function FieldIssuesList({ issues = [] }) {
  return (
    <Box sx={styles.issueListBox}>
      <ul style={styles.issueUl}>
        {issues.map((msg, i) => (
          <li key={i} style={styles.issueLi}>
            <Typography sx={{ color: '#B42318', fontSize: '0.85rem', lineHeight: 1.2 }}>
              {msg}
            </Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
}

export default function ConfigTable({
  rows = [],
  onRemove,
  onStartEdit,
  editingIdx,
  codeToName,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];

  const columns = useMemo(() => {
    return HEAD_CELLS.map((header) => {
      const field = header;
      const label = prettifyHeader(header);

      const common = {
        field,
        label,
        sortable: true,
        align: header === 'Comments' ? 'left' : 'left',
        cellSx: { position: 'relative' },
      };

      switch (header) {
        case 'OperatorName':
          return {
            ...common,
            sortable: false,
            render: (row) => codeToName?.get(row?.Operator) ?? '—',
          };
        case 'MeterType':
          return {
            ...common,
            render: (row) => labelForType(row?.MeterType) ?? '—',
          };
        case 'FromDate':
          return {
            ...common,
            type: 'date',
            render: (row) => {
              if (!row?.FromDate) return '—';
              const d = parseUKDate(row.FromDate);
              return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
            },
          };
        case 'Comments':
          return {
            ...common,
            render: (row) => (row?.Comments ? String(row.Comments) : '—'),
            cellSx: { position: 'relative', whiteSpace: 'normal', wordBreak: 'break-word' },
          };
        default:
          return {
            ...common,
            render: (row) => {
              const raw = row?.[field];
              return raw ?? raw === 0 ? String(raw) : '—';
            },
          };
      }
    });
  }, [codeToName]);

  const actionsRenderer = (row) => {
    const originalIdx = row?._originalIndex ?? null;
    const isEditing = editingIdx === originalIdx;

    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {!isEditing && (
          <IconButton
            size="small"
            onClick={() => onStartEdit?.(originalIdx)}
            aria-label="Edit row"
            sx={styles.compactActionButton}
            data-testid={`edit-${originalIdx}`}
          >
            <EditIcon sx={styles.editIcon} fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={() => onRemove?.(originalIdx)}
          aria-label="Delete row"
          sx={styles.compactActionButton}
          data-testid={`delete-${originalIdx}`}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const columnsWithValidation = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      render: (row, rawValue) => {
        const issues = Array.isArray(row?.issues) ? row.issues.filter(Boolean) : [];
        const hasIssues = row?.Validated === false && issues.length > 0;

        const cellHeader = col.field;
        const relatedErrors = hasIssues ? issuesForHeader(cellHeader, issues) : [];

        const baseValue = col.render ? col.render(row, rawValue) : rawValue;

        if (hasIssues && relatedErrors.length > 0) {
          return (
            <Tooltip
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#fff',
                    color: 'inherit',
                    boxShadow: 1,
                    px: 0.5,
                    py: 0.5,
                    fontSize: '0.85rem',
                    maxWidth: 260,
                  },
                },
                arrow: { sx: { color: '#fff' } },
              }}
              title={<FieldIssuesList issues={relatedErrors} />}
              arrow
              placement="bottom-start"
            >
              <Box sx={styles.invalidCellFull} role="presentation">
                <Typography sx={styles.errorCellText}>{baseValue}</Typography>
              </Box>
            </Tooltip>
          );
        }

        if (hasIssues) {
          return baseValue;
        }

        return baseValue;
      },
    }));
  }, [columns]);

  const getRowId = (r, i) => r?.ID ?? r?.FileIncomingID ?? r?._originalIndex ?? String(i);

  const defaultSort = { field: null, direction: 'asc' };

  const rowSx = (row) => {
    const issues = Array.isArray(row?.issues) ? row.issues.filter(Boolean) : [];
    const hasIssues = row?.Validated === false && issues.length > 0;
    return hasIssues ? styles.invalidRowStrongRow : undefined;
  };

  return (
    <Box sx={{ ...tableStyles.tableWrapper, flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Table
        rows={safeRows}
        columns={columnsWithValidation}
        actionsRenderer={actionsRenderer}
        actionsHeader={null}
        stickyHeader
        zebra
        hover
        bordered
        getRowId={getRowId}

        defaultSort={defaultSort}
        onSortChange={() => {}}
        rowSx={rowSx}
      />
    </Box>
  );
}
